import React, {useCallback, useEffect, useState} from 'react';
import {List} from 'immutable';
import {ExternalCallCardSource} from '@store/call-card/external/ExternalCallCardSource';
import {ResponseStatus} from 'utils/ResponseStatus';
import NotFoundPage from '@components/not-found';
import {ExternalCallLogin} from '@components/call-card/external/ExternalCallLogin';
import Loading from 'components/pure/utils/Loading';
import ExternalCallCardContainer from '@components/call-card/external/ExternalCallCardContainer';
import {CoachLogo} from '@components/logo/CoachLogo';
import './ExternalSharePage.styl.sass';
import {SharedAreas} from '@components/call-card/SharedHighlights.utils';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import AuthStore from 'stores/AuthStore';
import {browserHistory} from 'react-router';
import MailActions from 'actions/inbox/MailActions';

interface ExternalSharePageProps {
  params: {
    permalink: string;
  };
}

export const ExternalSharePage: React.FC<ExternalSharePageProps> = ({params}) => {
  const [status, setStatus] = useState<ResponseStatus>();
  const [callId, setCallId] = useState<FileUploadId>();
  const [sharedAreas, setSharedAreas] = useState<SharedAreas>(List());
  const [bundleError, setBundleError] = useState(false);
  const login = useCallback(
    async (password?: string) => {
      const permalink = params.permalink;
      const {status, callId, sharedAreas, accountId} = await ExternalCallCardSource.login(
        permalink,
        password
      );
      // If the user is logged in, and the call is in their account then send them to the normal call card.
      // Also mark their inbox items as viewed
      if (AuthStore.getState().user?.accountId === accountId) {
        MailActions.markCallAsRead(callId);
        browserHistory.push(`/call/${callId}`);
      }

      setStatus(status);
      setSharedAreas(sharedAreas);

      // Fetch and hydrate all call data before setting callId.
      // Setting callId triggers a render that mounts ExternalCallCardContainer,
      // so the store must be populated first to avoid redundant API calls.
      if (status === ResponseStatus.OK && callId) {
        try {
          const bundle = await ExternalCallCardSource.getBundle(permalink);
          ExternalCallCardSource.hydrateStoreFromBundle(bundle, callId);
        } catch (e) {
          console.error('Failed to fetch external share bundle', e);
          setBundleError(true);
        }
      }
      setCallId(callId);
    },
    [params.permalink]
  );

  useEffect(() => {
    login().catch(console.error);
  }, [login]);

  if (!status) {
    return null;
  }

  if (bundleError) {
    return <NotFoundPage>Something went wrong loading this shared call.</NotFoundPage>;
  }

  if (status === ResponseStatus.NOT_FOUND) {
    return <NotFoundPage>We could not find the share that you were looking for.</NotFoundPage>;
  }

  if (status === ResponseStatus.GONE) {
    return (
      <NotFoundPage title="410" subtitle="Expired">
        The share you were looking for has expired.
      </NotFoundPage>
    );
  }

  // Share requires a password, or the password tried was incorrect.
  if (status === ResponseStatus.FORBIDDEN || status === ResponseStatus.UNAUTHORIZED) {
    return (
      <ExternalCallLogin onLogin={login} incorrectPassword={status === ResponseStatus.FORBIDDEN} />
    );
  }

  if (!callId) return <Loading />;

  return (
    <div className="external-share-page">
      <nav className="external-navbar">
        <CoachLogo />
      </nav>
      <ExternalCallCardContainer callId={callId} sharedAreas={sharedAreas} />
    </div>
  );
};
